import React from 'react';
import { Header } from '../components/Header';
import { Footer } from '../components/Footer';
import { PageHero } from '../components/blocks/PageHero';
import { MediaCard } from '../components/blocks/MediaCard';
import { CtaBanner } from '../components/blocks/CtaBanner';
import { SectionHeading } from '../components/blocks/SectionHeading';
import { SectionDivider, SURFACE } from '../components/blocks/SectionDivider';
import { useSeo } from '../lib/useSeo';
import { audioVideoSchema, faqSchema } from '../lib/schema';

/**
 * PAGE: /guided-meditations
 * WORDPRESS: page template `page-guided-meditations.php`
 *
 * Blocks: hero → intro_statement → media_card[] → cta_banner
 */

const HERO_IMAGE = 'https://cdn.magicpatterns.com/uploads/9bpHS6JKYC6Ng4QC1wzJJi/milangela_meditation.webp';

/** ACF: `meditation` CPT / repeater `meditation_library` */
const meditations = [{
  id: '87JRrd0RlTg',
  title: "Meditation to restore your energy — Milangela's meditation",
  description: 'A guided practice to help you release fatigue and gently restore your natural energy.',
  url: 'https://www.youtube.com/watch?v=87JRrd0RlTg',
  /** sddefault is ~10x smaller than maxresdefault and identical at card size. */
  thumbnail: 'https://img.youtube.com/vi/87JRrd0RlTg/sddefault.jpg'
}];

/** ACF: `page_faqs` repeater — powers FAQPage schema for AI search (GEO). */
const faqs = [{
  question: 'Are the guided meditations free?',
  answer: 'Yes. Every guided meditation published in The Garden is free to watch and listen to as often as you like.'
}, {
  question: 'Do I need experience to follow along?',
  answer: 'No experience is needed. Each practice is fully guided from start to finish, so you can simply listen and follow.'
}];
export function Meditation() {
  useSeo({
    title: 'Guided Meditations',
    description: 'Free guided meditation practices to help you slow down, reconnect with your body and create moments of stillness in everyday life.',
    path: '/guided-meditations',
    image: HERO_IMAGE,
    breadcrumbs: [{
      name: 'Guided Meditations',
      path: '/guided-meditations'
    }],
    schema: [...meditations.map(item => audioVideoSchema({
      type: 'VideoObject',
      name: item.title,
      description: item.description,
      path: '/guided-meditations',
      image: item.thumbnail,
      externalUrl: item.url
    })), faqSchema(faqs)]
  });
  return <div className="min-h-screen bg-cream" data-id="element-553" data-mp="x43whxkm438r">
      <Header data-id="element-554" data-mp="x32jvony78ox" />

      <main data-id="element-555" data-mp="1bu1nsmdu9926">
        <PageHero eyebrow="The Garden" heading="Guided Meditations" subheading="A quiet space to return to yourself." body="Meditation is not about escaping life — it is about meeting it with greater awareness." image={HERO_IMAGE} imageAlt="Milangela taking a quiet moment of meditation in the garden" objectPosition="62% center" dividerFill={SURFACE.cream} data-id="element-556" data-mp="1bukwdchlml3j" />

        <section className="bg-cream px-6 py-20 md:py-28" data-id="element-557" data-mp="1zi809xq7b7t1">
          <div className="mx-auto max-w-3xl text-center" data-id="element-558" data-mp="24gro63ocuto1">
            <p className="text-xs font-medium tracking-[0.26em] text-sage-700 uppercase" data-id="element-559" data-mp="24e5he69agi6v">
              A moment of stillness
            </p>
            <div className="mt-6 space-y-5 font-serif text-2xl leading-snug text-charcoal sm:text-3xl" data-id="element-560" data-mp="1vf1vkuiqlooo">
              <p data-id="element-561" data-mp="1gpw09hn0sim2">
                These guided practices are here to help you slow down, reconnect
                with your body and create moments of stillness in everyday life.
              </p>
              <p className="italic text-sage-700" data-id="element-562" data-mp="17r1y7bqx7014">
                Return whenever you wish to breathe, soften and simply be.
              </p>
            </div>
          </div>
        </section>

        <SectionDivider from={SURFACE.cream} to={SURFACE.creamDeep} data-id="element-563" data-mp="xrt6kpjj1xhx" />

        <section className="bg-cream-200 px-6 pb-24 pt-8 md:pb-28" data-id="element-564" data-mp="12r60tvcqa6uj">
          <div className="mx-auto max-w-4xl" data-id="element-565" data-mp="12qe42h7ea32o">
            <SectionHeading eyebrow="Guided practice" heading="Meditation library" align="center" accent="rose" className="mb-12" data-id="element-566" data-mp="2553kv5cpdj6r" />
            <div className="space-y-8" data-id="element-567" data-mp="1wbgxa2infm8n">
              {meditations.map(item => <MediaCard key={item.id} title={item.title} thumbnail={item.thumbnail} thumbnailAlt={item.title} url={item.url} linkLabel="Watch on YouTube" data-id="element-568" data-mp="1rdp3t42cvs2c" />)}
            </div>
          </div>
        </section>

        <CtaBanner heading="Would you like to experience this in person?" buttonLabel="Start Your Journey" buttonTo="/start-your-journey" dividerFrom={SURFACE.creamDeep} data-id="element-569" data-mp="1ddo1rlacuela" />
      </main>

      <Footer data-id="element-570" data-mp="32sltmg6d6lar" />
    </div>;
}